import type { ConnectionType, ContainerKind, ObjectId, Size, SpatialObjectKind, Vec2 } from '@/types';
import type { SceneGraph } from '@/core/scene/sceneGraph';
import { unionBounds } from '@/core/camera/cameraOps';
import { screenToWorld } from '@/core/coordinates/coordinateSystem';
import { useWorkspaceStore, selectActiveDocument } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useCameraStore } from '@/store/cameraStore';
import {
  addObjectsCommand,
  bringToFrontCommand,
  deleteObjectsCommand,
  duplicateObjectsCommand,
  groupObjectsCommand,
  moveObjectsCommand,
  nextZIndex,
  sendToBackCommand,
  setLockedCommand,
  setVisibilityCommand,
  ungroupCommand,
  setCollapsedCommand,
  type FrameResolver,
} from './objectCommands';
import { addConnectionCommand, deleteConnectionsCommand } from '@/features/connections/connectionCommands';
import { createFolder, createGroup, createObjectOfKind } from './objectFactory';
import type { ApplicationRegistry } from '@/applications';

/**
 * User-level actions that combine stores, scene graph and commands. Components and
 * keyboard shortcuts call these; they never assemble commands themselves.
 */

const doc = () => selectActiveDocument(useWorkspaceStore.getState());
const dispatch = (c: Parameters<ReturnType<typeof useWorkspaceStore.getState>['dispatch']>[0] | null) => {
  if (c) useWorkspaceStore.getState().dispatch(c);
};
const selectedIds = () => useSelectionStore.getState().selection.objectIds;
const frames =
  (scene: SceneGraph): FrameResolver =>
  (id) =>
    scene.worldMatrixOf(id);

/** World position at the centre of the viewport, lightly jittered so stacks stay visible. */
export const viewportCentreWorld = (size: Size): Vec2 => {
  const { camera, viewport } = useCameraStore.getState();
  const p = screenToWorld({ x: viewport.size.width / 2, y: viewport.size.height / 2 }, camera);
  const jitter = (Math.random() - 0.5) * 60;
  return { x: Math.round(p.x - size.width / 2 + jitter), y: Math.round(p.y - size.height / 2 + jitter) };
};

export const addObject = (kind: SpatialObjectKind, applications: ApplicationRegistry): ObjectId | null => {
  const d = doc();
  const [app] = applications.list();
  if (!d || !app) return null;
  const probe = createObjectOfKind(kind, {
    workspaceId: d.workspace.id,
    position: { x: 0, y: 0 },
    zIndex: 0,
    applicationId: app.id,
  });
  const input = {
    workspaceId: d.workspace.id,
    position: viewportCentreWorld(probe.size),
    zIndex: nextZIndex(d),
    applicationId: app.id,
  };
  const obj = createObjectOfKind(kind, kind === 'window' ? { ...input, name: app.name } : input);
  dispatch(addObjectsCommand([obj]));
  useSelectionStore.getState().select([obj.id]);
  return obj.id;
};

export const deleteSelection = (): void => {
  const d = doc();
  const ids = selectedIds();
  const connectionIds = useSelectionStore.getState().selection.connectionIds;
  if (!d) return;
  if (ids.length) dispatch(deleteObjectsCommand(d, ids));
  else if (connectionIds.length) dispatch(deleteConnectionsCommand(d, connectionIds));
  else return;
  useSelectionStore.getState().clear();
};

/** Collapses or expands every selected container. */
export const setSelectionCollapsed = (collapsed: boolean): void => {
  for (const id of selectedIds()) {
    const d = doc();
    const cmd = d && setCollapsedCommand(d, id, collapsed);
    if (cmd) dispatch(cmd);
  }
};

/** Connects every selected object to the first one, in order (a fan-out), with the given type. */
export const connectSelection = (type: ConnectionType): void => {
  const ids = selectedIds();
  const [first, ...rest] = ids;
  if (!first || !rest.length) return;
  for (const target of rest) {
    const d = doc();
    const cmd = d && addConnectionCommand(d, first, target, { type });
    if (cmd) dispatch(cmd);
  }
};

export const duplicateSelection = (): void => {
  const d = doc();
  const ids = selectedIds();
  if (!d || !ids.length) return;
  const result = duplicateObjectsCommand(d, ids);
  if (!result) return;
  dispatch(result.command);
  // Select only the copies of what was selected (not their descendants).
  const copied = result.newIds.slice(0, ids.length);
  useSelectionStore.getState().select(copied.length ? copied : result.newIds);
};

export const nudgeSelection = (delta: Vec2): void => {
  const ids = selectedIds();
  if (ids.length) dispatch(moveObjectsCommand(ids, delta));
};

export const groupSelection = (scene: SceneGraph, kind: ContainerKind = 'group'): void => {
  const d = doc();
  const ids = selectedIds();
  if (!d || ids.length === 0) return;
  const bounds = unionBounds(ids.map((id) => scene.boundsOf(id)).filter((b): b is NonNullable<typeof b> => !!b));
  if (!bounds) return;
  const result = groupObjectsCommand(d, ids, kind, bounds, frames(scene), (input) => {
    const make = kind === 'group' ? createGroup : createFolder;
    return { ...make({ workspaceId: d.workspace.id, ...input }), parentId: input.parentId };
  });
  if (!result) return;
  dispatch(result.command);
  useSelectionStore.getState().select([result.containerId]);
};

export const ungroupSelection = (scene: SceneGraph): void => {
  const d = doc();
  if (!d) return;
  const lifted: ObjectId[] = [];
  for (const id of selectedIds()) {
    const current = selectActiveDocument(useWorkspaceStore.getState());
    if (!current) break;
    const children = Object.values(current.objects)
      .filter((o) => o.parentId === id)
      .map((o) => o.id);
    const cmd = ungroupCommand(current, id, frames(scene));
    if (cmd) {
      dispatch(cmd);
      lifted.push(...children);
    }
  }
  if (lifted.length) useSelectionStore.getState().select(lifted);
};

export const bringSelectionToFront = (): void => {
  const d = doc();
  const ids = selectedIds();
  if (d && ids.length) dispatch(bringToFrontCommand(d, ids));
};

export const sendSelectionToBack = (): void => {
  const d = doc();
  const ids = selectedIds();
  if (d && ids.length) dispatch(sendToBackCommand(d, ids));
};

export const toggleSelectionLocked = (): void => {
  const d = doc();
  const ids = selectedIds();
  if (!d || !ids.length) return;
  const anyUnlocked = ids.some((id) => d.objects[id]?.locked === false);
  dispatch(setLockedCommand(d, ids, anyUnlocked));
};

export const hideSelection = (): void => {
  const d = doc();
  const ids = selectedIds();
  if (d && ids.length) dispatch(setVisibilityCommand(d, ids, false));
};

export const showAll = (): void => {
  const d = doc();
  if (!d) return;
  dispatch(setVisibilityCommand(d, Object.keys(d.objects) as ObjectId[], true));
};

export const selectAllObjects = (): void => {
  const d = doc();
  if (d)
    useSelectionStore
      .getState()
      .selectAll((Object.keys(d.objects) as ObjectId[]).filter((id) => d.objects[id]?.visible));
};

export const invertSelection = (): void => {
  const d = doc();
  if (d)
    useSelectionStore.getState().invert((Object.keys(d.objects) as ObjectId[]).filter((id) => d.objects[id]?.visible));
};
